import {
	EntriesParams,
	Entry,
	EntryIds,
	EntryList,
	Feedback,
	FeedbackParams,
	Flashcard,
	FlashcardSet,
	ListResponse,
	LookupResponse,
	SchoolClass,
	Search,
	Visibility,
} from '@/types';

import client from './client';

export function lookup(languages: string[], word: string): Promise<LookupResponse> {
	const params = new URLSearchParams({
		text: word,
	});
	for (const l of languages) {
		params.append('language', l);
	}
	return client.get(`/api/dictionary/lookup?${params.toString()}`).then(resp => resp.json());
}

export function findEntryByID(id: number): Promise<Entry> {
	return client.get(`/api/dictionary/entries/${id}`).then(resp => resp.json());
}

export function addFeedback(params: FeedbackParams): Promise<Feedback> {
	return client.post(`/api/dictionary/feedback`, { ...params }).then(resp => resp.json());
}

export function getSearchHistory(): Promise<ListResponse<Search>> {
	return client.get('/api/dictionary/user-search-history').then(resp => resp.json());
}

export function getFavorites(): Promise<EntryList> {
	return client.get(`/api/dictionary/entry-lists/favorites`).then(resp => resp.json());
}

export function getEntryListList(visibility: Visibility, languageCode?: string): Promise<ListResponse<EntryList>> {
	const params = new URLSearchParams();

	params.set('visibility', String(visibility));

	if (languageCode) {
		params.set('language', languageCode);
	}

	return client.get(`/api/dictionary/entry-lists-user?${params.toString()}`).then(resp => resp.json());
}

export function getEntryList(id: number): Promise<EntryList> {
	return client.get(`/api/dictionary/entry-lists-user/${id}`).then(resp => resp.json());
}

export function createEntryList(entryList: EntryList): Promise<EntryList> {
	const params = {
		name: entryList.name,
		language: entryList.language ? entryList.language.code : null,
		visibility: entryList.visibility ?? 'private',
		entries: entryList.entries ?? null,
	};
	return client.post(`/api/dictionary/entry-lists`, { ...params }).then(resp => resp.json());
}

export function updateEntryList(entry_list_id: number, entryList: EntryList): Promise<EntryList> {
	const language = entryList.language ? entryList.language.code : null;

	const params = {
		name: entryList.name,
		language: language,
		entries: entryList.entries,
		visibility: entryList.visibility,
		published: true,
	};
	return client.put(`/api/dictionary/entry-lists/${entry_list_id}`, { ...params }).then(resp => resp.json());
}

export function deleteEntryList(id: number): Promise<EntryList> {
	return client.delete(`/api/dictionary/entry-lists/${id}`).then(resp => resp.json());
}

export function addEntryToEntryList(entry_list_id: number, entry: Entry): Promise<EntryList> {
	const entriesParameter = {
		entries: [entry],
	};

	return addEntriesToEntryList(entry_list_id, entriesParameter);
}

export function addEntriesToEntryList(entry_list_id: number, entries: EntriesParams): Promise<EntryList> {
	return client.post(`/api/dictionary/entry-lists/${entry_list_id}`, { ...entries }).then(resp => resp.json());
}

export function addEntryToFavorites(entry: Entry) {
	const entriesParams = {
		entries: [entry],
	};

	return addEntriesToFavorites(entriesParams);
}

export function removeEntriesFromEntryList(entry_list_id: number, entries: EntryIds): Promise<EntryList> {
	return client
		.delete(`/api/dictionary/entry-lists/${entry_list_id}/remove-entries`, { ...entries })
		.then(resp => resp.json());
}

export function addEntriesToFavorites(entries: EntriesParams): Promise<EntryList> {
	return client.post(`/api/dictionary/entry-lists/favorites`, { ...entries }).then(resp => resp.json());
}

export function removeEntriesFromFavorites(entryIds: number[]): Promise<EntryList> {
	const entries = {
		ids: entryIds,
	};
	return client
		.delete(`/api/dictionary/entry-lists/remove-from-favorites`, { ...entries })
		.then(resp => resp.json());
}

export function getEntryListSchoolClasses(entry_list_id: number): Promise<SchoolClass[]> {
	return client.get(`/api/dictionary/entry-lists/${entry_list_id}/school-classes`).then(resp => resp.json());
}

export function storeEntryListSchoolClasses(entry_list_id: number, schoolClasses: SchoolClass[]): Promise<Response> {
	const props = {
		school_classes: schoolClasses,
	};

	return client
		.post(`/api/dictionary/entry-lists/${entry_list_id}/school-classes`, { ...props })
		.then(resp => resp.json());
}

export function listFlashcardSets(visibility: Visibility, languageCode?: string): Promise<ListResponse<FlashcardSet>> {
	const params = new URLSearchParams();

	params.set('visibility', String(visibility));

	if (languageCode) {
		params.set('language', languageCode);
	}

	return client.get(`/api/dictionary/flashcard-set-user?${params.toString()}`).then(resp => resp.json());
}

export function getFlashcardSet(id: number): Promise<FlashcardSet> {
	return client.get(`/api/dictionary/flashcard-set/${id}`).then(resp => resp.json());
}

export function createFlashcardSet(set: FlashcardSet): Promise<FlashcardSet> {
	const params = makeFlashcardSetParams(set);

	return client.post(`/api/dictionary/flashcard-set`, { ...params }).then(resp => resp.json());
}

export function updateFlashcardSet(id: number, set: FlashcardSet): Promise<FlashcardSet> {
	const params = makeFlashcardSetParams(set);

	return client.put(`/api/dictionary/flashcard-set/${id}`, { ...params }).then(resp => resp.json());
}

export function deleteFlashcardSet(id: number): Promise<FlashcardSet> {
	return client.delete(`/api/dictionary/flashcard-set/${id}`).then(resp => resp.json());
}

export function createFlashcard(setId: number, card: Flashcard): Promise<Flashcard> {
	const params = makeFlashcardParams(card);

	return client.post(`/api/dictionary/flashcard-set/${setId}/add`, { ...params }).then(resp => resp.json());
}

export function updateFlashcard(setId: number, card: Flashcard): Promise<Flashcard> {
	const params = makeFlashcardParams(card);

	return client
		.put(`/api/dictionary/flashcard-set/${setId}/flashcard/${card.id}`, { ...params })
		.then(resp => resp.json());
}

export function deleteFlashcard(setId: number, card: Flashcard): Promise<Flashcard> {
	return client.delete(`/api/dictionary/flashcard-set/${setId}/flashcard/${card.id}`).then(resp => resp.json());
}

export function getFlashcardSetSchoolClasses(flashcard_set_id: number): Promise<SchoolClass[]> {
	return client.get(`/api/dictionary/flashcard-set/${flashcard_set_id}/school-classes`).then(resp => resp.json());
}

export function storeFlashcardSetSchoolClasses(
	flashcard_set_id: number,
	schoolClasses: SchoolClass[],
): Promise<Response> {
	const props = {
		school_classes: schoolClasses,
	};

	return client
		.post(`/api/dictionary/flashcard-set/${flashcard_set_id}/school-classes`, { ...props })
		.then(resp => resp.json());
}

function makeFlashcardSetParams(set: FlashcardSet) {
	let flashcardParams;

	if (set.flashcards) {
		flashcardParams = new Array(set.flashcards.length);

		for (let i = 0; i < set.flashcards.length; i++) {
			flashcardParams[i] = makeFlashcardParams(set.flashcards[i]);
		}
	}

	return {
		Language: set.language,
		Visibility: set.visibility,
		Name: set.name,
		Description: set.description,
		Flashcards: flashcardParams ?? null,
		EntryListID: set.entryListId,
		Randomize: set.randomize,
		Published: set.published,
	};
}

function makeFlashcardParams(card: Flashcard) {
	return {
		id: card.id,
		QuestionText: card.questionText,
		CardType: card.cardType,
		Data: card.data,
		EntryId: card.entry?.id,
		Order: card.order,
		Active: card.active,
		BottomSideUp: card.bottomSideUp,
	};
}
